import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  DocumentReference,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import chunk from 'lodash/chunk';
import { userAsPromise } from '../providers/userProvider';
import { IAthlete, IGroup, ITeam, IUser } from '../types';

/**
 * @description updates an existing team and adds/removes athletes and groups
 * @param team
 * @param athletes
 * @param groups
 */
const updateTeam = async (
  team: Partial<ITeam>,
  selectedAthletes: IAthlete[],
  allAthletes: IAthlete[],
  selectedGroups: IGroup[],
  allGroups: IGroup[]
): Promise<void[]> => {
  const user = await userAsPromise();

  await updateDoc(
    doc(user.services.firestore, `/orgs/${user.orgId}/teams/${team.id}`),
    team
  );
  await updateTeamEntities(
    user,
    team.id,
    selectedAthletes,
    selectedGroups,
    'add'
  );

  /// determine if there any athletes that are no longer members of this team
  const nowExcludedAthletes = allAthletes.filter((a: IAthlete): boolean => {
    return (
      !selectedAthletes.map((ath: IAthlete): string => ath.id).includes(a.id) &&
      a.teams?.includes(team.id)
    );
  });

  /// determine if there any groups that are no longer members of this team
  const nowExcludedGroups = allGroups.filter((g: IGroup): boolean => {
    return (
      !selectedGroups.map((grp: IGroup): string => grp.id).includes(g.id) &&
      g.teams?.includes(team.id)
    );
  });

  return updateTeamEntities(
    user,
    team.id,
    nowExcludedAthletes,
    nowExcludedGroups,
    'remove'
  );
};

/**
 * @description creates a new team and adds any supplied groups or athletes to the team
 * @param team
 */
const addTeam = async (
  team: Partial<ITeam>,
  athletes: IAthlete[],
  groups: IGroup[],
  aggregateOnCreate: boolean
): Promise<void[]> => {
  const user = await userAsPromise();

  const ref = doc(
    collection(user.services.firestore, `/orgs/${user.orgId}/teams`)
  );

  await setDoc(
    doc(user.services.firestore, `/orgs/${user.orgId}/teams/${ref.id}`),
    {
      ...team,
      id: ref.id,
      aggregateOnCreate,
    }
  );
  return updateTeamEntities(user, ref.id, athletes, groups, 'add');
};

type UpdateEntityAction = 'add' | 'remove';

/**
 * @description updates groups and athletes team membership
 * @param teamId
 * @param athletes
 * @param groups
 */

const updateTeamEntities = async (
  user: IUser,
  teamId: string,
  athletes: IAthlete[],
  groups: IGroup[],
  action: UpdateEntityAction
): Promise<void[]> => {
  /// perform updates in batches to avoid firestore write limit
  const athleteUpdateBatches = chunk(athletes, 200);
  const athleteBatchOperations = athleteUpdateBatches.map(
    (updatesInBatch: IAthlete[]): Promise<void> => {
      let athletesToUpdate: IAthlete[];
      if (action === 'remove') {
        /// if removing athletes need to check to make sure the athlete is a member of at least one other team
        athletesToUpdate = updatesInBatch.filter((a: IAthlete): boolean => {
          return a.teams.length > 1;
        });
      } else {
        athletesToUpdate = updatesInBatch;
      }
      const refs = athletesToUpdate.map(
        (athlete: IAthlete): DocumentReference =>
          doc(
            user.services.firestore,
            `orgs/${user.orgId}/athletes/${athlete.id}`
          )
      );
      const batch = writeBatch(user.services.firestore);

      for (const ref of refs) {
        batch.update(ref, {
          teams: action === 'add' ? arrayUnion(teamId) : arrayRemove(teamId),
        });
      }
      return batch.commit();
    }
  );
  const groupUpdateBatches = chunk(groups, 200);
  const groupBatchOperations = groupUpdateBatches.map(
    (updatesInBatch: IGroup[]): Promise<void> => {
      const refs = updatesInBatch.map(
        (group: IGroup): DocumentReference =>
          doc(user.services.firestore, `orgs/${user.orgId}/groups/${group.id}`)
      );
      const batch = writeBatch(user.services.firestore);

      for (const ref of refs) {
        batch.update(ref, {
          teams: action === 'add' ? arrayUnion(teamId) : arrayRemove(teamId),
        });
      }
      return batch.commit();
    }
  );

  return Promise.all([...athleteBatchOperations, ...groupBatchOperations]);
};

export { addTeam, updateTeam };
